import { useCallback } from 'react';
import { create } from 'zustand';

// A bulk-move dispatch session the FE polls to readiness (see
// DispatchSessionPoller). `startedAt` drives the poll deadline.
export type PendingDispatchSession = {
  sessionId: string;
  startedAt: number;
};

// Persisted to sessionStorage so a refresh mid-dispatch resumes polling
// against the original start — the session id is returned once (move
// response) and isn't on the bulk-activities list, so we'd otherwise lose it.
const STORAGE_KEY = 'underway:pendingDispatchSessions';

const loadSessions = (): PendingDispatchSession[] => {
  if (typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (s): s is PendingDispatchSession =>
        typeof s?.sessionId === 'string' && typeof s?.startedAt === 'number'
    );
  } catch {
    return [];
  }
};

const persistSessions = (sessions: PendingDispatchSession[]) => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  } catch {
    // Ignore write errors (quota, private mode) — durability is best-effort.
  }
};

type DispatchSessionState = {
  sessions: PendingDispatchSession[];
  addSession: (session: PendingDispatchSession) => void;
  removeSession: (sessionId: string) => void;
};

export const useDispatchSessionStore = create<DispatchSessionState>((set) => ({
  sessions: loadSessions(),
  addSession: (session) =>
    set((state) => {
      // Dedup by id so a re-render/double-submit can't spawn two pollers.
      if (state.sessions.some((s) => s.sessionId === session.sessionId)) {
        return state;
      }
      const next = [...state.sessions, session];
      persistSessions(next);
      return { sessions: next };
    }),
  removeSession: (sessionId) =>
    set((state) => {
      const next = state.sessions.filter((s) => s.sessionId !== sessionId);
      if (next.length === state.sessions.length) return state;
      persistSessions(next);
      return { sessions: next };
    }),
}));

// Stable callback for the bulk-move success handler — stamps `startedAt`
// and registers. No-ops on a blank id (single / skip-automation moves).
export const useRegisterDispatchSession = () => {
  const addSession = useDispatchSessionStore((s) => s.addSession);
  return useCallback(
    (sessionId: string | undefined | null) => {
      if (!sessionId) return;
      addSession({ sessionId, startedAt: Date.now() });
    },
    [addSession]
  );
};
